import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { ResponsiveSidebarDialog, ModalTitle, ModalContent, StickyModalActions, PrimaryButton } from '@/styles/blocks';

import { Button } from '@onward-delivery/design/button';
import { Card } from '@onward-delivery/design/card';
import { calendarDate } from '@onward-delivery/design/calendar-utils';
import { DESIGN_SCOPE_CLASS } from '@onward-delivery/design/scope';

import { Context } from './store';
import { useFormatters } from './hooks';
import { DesignScope, Header2, Body2, SectionHeader, BORDER, GREY_TEXT, TOKENS, fillColor } from './blocks';

import { FillBar, LockSwitch, Meta, PercentBadge, ResourceName, StatTile } from './calendarBlocks';
import { FILL_STATES, emptyBillingSummary, ceilingBreakdown, formatTimeframe } from './dayMetrics';
import { VEHICLE_TYPES } from './constants';
import {
    LOCK_HINT,
    effectiveAccepting,
    hasPendingChange,
    hasWrites,
    isLocked,
    lockAllLabel,
    lockAllPending,
    resourceKey,
    schedulingWrites,
    toggleDisabled,
} from './schedulingLocks';

const VEHICLE_LABELS = Object.fromEntries(VEHICLE_TYPES.map(({ value, label }) => [value, label]));

const MetricCell = ({ entry }) => (
    <Grid
        item
        css={css`
            flex: 1;
            flex-basis: 0;
            min-width: 80px;
        `}
    >
        <Meta>{entry.ceiling.label}</Meta>
        <Body2
            css={css`
                display: block;
                font-weight: ${entry.isPrimary ? 700 : 500};
            `}
        >
            {entry.limit
                ? `${entry.used.toLocaleString()} / ${entry.limit.toLocaleString()}`
                : entry.used.toLocaleString()}
        </Body2>
        <Meta>
            {entry.limit ? `${entry.percent}% · ${entry.ceiling.short}` : `${entry.ceiling.short} · no ceiling`}
        </Meta>
    </Grid>
);

const ResourceCard = ({ resource, load, fill, accepting = true, lockable, locked, lockDisabled, onLockChange }) => {
    const breakdown = ceilingBreakdown({ resource, load });
    const timeframe = formatTimeframe(resource.timeframe_start, resource.timeframe_end);
    const meta = [VEHICLE_LABELS[resource.vehicle_type], timeframe].filter(Boolean).join(' · ');

    return (
        <Card
            className="p-[12px_14px]"
            css={css`
                margin-bottom: 12px;

                ${accepting
                    ? ''
                    : `
                background-color: ${TOKENS.muted}14;
                border-color: ${TOKENS.border};
                `}
            `}
        >
            <Grid
                container
                direction="row"
                css={css`
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: nowrap;
                    gap: 8px;
                `}
            >
                <ResourceName>{resource.name}</ResourceName>
                {/* The fill badge used to sit here; it reads above the bar it describes now. This
                    slot is the day's lock, which is the only thing on the card that is an action
                    at all — the card itself no longer opens the resource config dialog, so the
                    switch needs no stopPropagation to survive a click.

                    The switch reads `accepting`, not `locked`: green is a resource open for
                    business, which is the direction the old toggle ran in and the direction the
                    card mutes in. The label says what pressing it does, so the two do not say the
                    same thing twice. */}
                {lockable ? (
                    <LockSwitch disabled={lockDisabled} checked={!locked} onChange={() => onLockChange(!locked)} />
                ) : null}
            </Grid>

            {/* One row: the card's own subtitle on the left, the lock's label right-aligned under
                the switch it belongs to. Same size and colour, so they read as one line. */}
            <Grid
                container
                direction="row"
                css={css`
                    align-items: baseline;
                    justify-content: space-between;
                    flex-wrap: nowrap;
                    gap: 12px;
                `}
            >
                <Meta>{meta}</Meta>
                {lockable ? (
                    <Meta
                        css={css`
                            flex: 0 0 auto;
                            text-align: right;
                            font-weight: 700;
                            opacity: ${lockDisabled ? 0.6 : 1};
                        `}
                    >
                        {/* A disabled switch has no action to name, so the reason takes the slot. */}
                        {lockDisabled ? LOCK_HINT : `${locked ? 'Unlock' : 'Lock'} scheduling for this day`}
                    </Meta>
                ) : null}
            </Grid>
            {/* Always in the layout, hidden rather than unmounted: locking a resource must not
                change the card's height, or every card below it shifts as the operator works down
                the list. It costs one line on every card, which is what the ceiling callout that
                used to sit here was spending anyway. */}
            <Meta
                css={css`
                    color: ${TOKENS.warning};
                    font-weight: 700;
                    visibility: ${accepting ? 'hidden' : 'visible'};
                `}
            >
                Working, but not taking new orders
            </Meta>

            {fill.state === FILL_STATES.UNCONFIGURED ? (
                <Meta
                    css={css`
                        margin-top: 2px;
                    `}
                >
                    No Ceilings Configured
                </Meta>
            ) : (
                <div
                    css={css`
                        margin-top: 2px;
                    `}
                >
                    {/* Text, not a badge: right-aligned above the bar it describes, so it reads as
                        that bar's number rather than as a chip on the resource. It keeps the fill
                        colour, which is the only thing the badge was carrying that the number
                        does not. */}
                    <Meta
                        css={css`
                            display: block;
                            text-align: right;
                            margin-bottom: 4px;
                            font-weight: 700;
                            color: ${fillColor(fill.percent)};
                        `}
                    >
                        {fill.state === FILL_STATES.FULL ? 'Full' : `${fill.percent}% full`}
                    </Meta>
                    <FillBar percent={fill.percent} size="md" />
                    <Meta
                        css={css`
                            margin-top: 4px;
                        `}
                    >
                        {fill.used.toLocaleString()} / {fill.limit.toLocaleString()} {fill.ceiling.short} ·{' '}
                        {fill.ceiling.label} ceiling
                    </Meta>
                </div>
            )}

            <Grid
                container
                direction="row"
                css={css`
                    margin-top: 12px;
                    padding-top: 10px;
                    border-top: 1px solid ${BORDER};
                    flex-wrap: wrap;
                    gap: 8px;
                `}
            >
                {breakdown.map((entry) => (
                    <MetricCell key={entry.ceiling.value} entry={entry} />
                ))}
            </Grid>
        </Card>
    );
};

// The same four buckets the day cards show, off the same derivation — the side dialog used to
// print the raw view statuses plus Cancelled, so the two disagreed about what a day contained.
//
// A day whose orders never completed has no billing row, so `billing` is nullable and the
// incomplete note carries the card on its own.
const BillingRow = ({ billing, buckets = [], incomplete = 0 }) => {
    const summary = billing ? { ...billing, incomplete } : emptyBillingSummary(incomplete);

    return (
        <>
            <SectionHeader
                css={css`
                    margin-top: 20px;
                `}
            >
                Billing
            </SectionHeader>
            <Card className="p-[12px_14px]">
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: wrap;
                        gap: 12px;
                    `}
                >
                    {buckets.map(({ key, label, count }) => (
                        <Grid
                            item
                            key={key}
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                                min-width: 90px;
                            `}
                        >
                            <Meta>{label}</Meta>
                            <Body2
                                css={css`
                                    display: block;
                                    font-weight: 700;
                                `}
                            >
                                {count}
                            </Body2>
                        </Grid>
                    ))}
                </Grid>
                {/* Kept as a note rather than a fifth tile: the count is already inside
                    "Not invoiced", and this says which part of it never closed out. */}
                {summary.incomplete > 0 ? (
                    <Meta
                        css={css`
                            margin-top: 10px;
                            color: ${TOKENS.destructive};
                            font-weight: 700;
                        `}
                    >
                        {summary.incomplete} of these never completed, so nothing on them can invoice yet
                    </Meta>
                ) : null}
            </Card>
        </>
    );
};

function DaySideDialog() {
    const { state, callbacks } = useContext(Context);
    const formatters = useFormatters();
    const dayData = state.viewingDay;

    if (!dayData) {
        return null;
    }

    const { dateKey, day, entries, aggregate, percent, measured, billing, buckets } = dayData;

    return (
        <DayDialogBody
            // Remount per day: staged locks belong to the day they were staged on, and switching
            // days without closing would otherwise carry them across.
            key={dateKey}
            dateKey={dateKey}
            // Past is read off the live today, so a tab left open overnight moves yesterday over.
            isPast={dateKey < state.todayKey}
            day={day}
            entries={entries}
            aggregate={aggregate}
            percent={percent}
            measured={measured}
            billing={billing}
            buckets={buckets}
            formatters={formatters}
            callbacks={callbacks}
        />
    );
}

// Split out so the staged locks can be keyed to the day — see the key above.
//
// A past day is read-only: its lock controls are hidden, since closing a day that already ran books
// nothing either way. Billing is the reverse — invoices only exist for work that has run, so on
// today and after the section would count every booked order as "Not invoiced".
function DayDialogBody({
    dateKey,
    isPast,
    day,
    entries,
    aggregate,
    percent,
    measured,
    billing,
    buckets,
    formatters,
    callbacks,
}) {
    // `{ [resource_id]: accepting }`, empty until something is touched. Staged rather than written
    // per toggle so Save is a single commit and closing the dialog discards the lot.
    const [pending, setPending] = useState({});
    const [saving, setSaving] = useState(false);

    const dirty = hasPendingChange(entries, pending);
    const writes = useMemo(() => schedulingWrites({ entries, pending, day }), [entries, pending, day]);

    const save = () => {
        if (!hasWrites(writes)) {
            setPending({});
            return;
        }

        setSaving(true);
        // A failed save resolves with `errors` (an ApolloError, not an array) rather than
        // rejecting, and the store has already shown the message — so the staged toggles stay put
        // for a retry rather than vanishing.
        Promise.resolve(callbacks.setSchedulingOverrides(writes))
            .then((result) => {
                if (!result?.errors) {
                    setPending({});
                }
            })
            .finally(() => setSaving(false));
    };

    return (
        <ResponsiveSidebarDialog
            // The body only mounts for a day that exists, so there is no closed state to render.
            open
            onClose={callbacks.clearViewDay}
            maxWidth="sm"
            fullWidth
            // ResponsiveSidebarDialog is a centred dialog, so anchoring right has to happen here.
            // Drawer would be the natural component but it is reserved for the app nav.
            css={css`
                .MuiDialog-container {
                    justify-content: flex-end;
                    align-items: stretch;
                }

                .MuiDialog-paper {
                    margin: 0;
                    width: 520px;
                    max-width: 100%;
                    height: 100vh;
                    max-height: 100vh;
                    border-radius: 0;
                }
            `}
        >
            <ModalTitle onClose={callbacks.clearViewDay}>
                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: nowrap;
                        gap: 12px;
                    `}
                >
                    <Header2
                        css={css`
                            color: #4c4c4c;
                        `}
                    >
                        {formatters.dayFull.format(calendarDate(dateKey))}
                    </Header2>
                    {/* MUI portals this dialog to document.body, outside the Calendar's scope, so
                        the design components inside it need their own. Scoped to the badge and the
                        content below rather than the whole paper: index.css's preflight substitute
                        would otherwise reset DialogTitle's h2 and the close button's svg. */}
                    {measured ? (
                        <DesignScope>
                            <PercentBadge size="lg" percent={percent}>
                                {percent}%
                            </PercentBadge>
                        </DesignScope>
                    ) : null}
                </Grid>
            </ModalTitle>
            <ModalContent
                className={DESIGN_SCOPE_CLASS}
                css={css`
                    width: 100%;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: nowrap;
                        border: 1px solid ${BORDER};
                        border-radius: 6px;
                        margin-bottom: 20px;
                    `}
                >
                    <StatTile label="Resources" value={aggregate.resources} />
                    <StatTile label="Stops" value={aggregate.stops} />
                    <StatTile label="Pieces" value={aggregate.pieces} />
                    <StatTile label="Day fill" value={measured ? `${percent}%` : '—'} subtext="fullest ceiling" last />
                </Grid>

                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: nowrap;
                        gap: 12px;
                        /* The header carries the gap on its own; here it sits on the row so the
                           button lines up with the text rather than with its margin. */
                        margin-bottom: 12px;
                    `}
                >
                    <SectionHeader
                        css={css`
                            margin-bottom: 0;
                        `}
                    >
                        Resources
                    </SectionHeader>
                    {/* The design system's Button rather than the footer's PrimaryButton: this row
                        is inside ModalContent, and the scoped preflight strips a MUI button back to
                        bare text in there — which is why the two looked nothing alike. Sized to
                        PrimaryButton's metrics (36px tall, 140px wide, 14px/700, no uppercase) so
                        the only difference left is the colour. tailwind-merge lets these win over
                        the variant's own classes. */}
                    {entries.length && !isPast ? (
                        <Button
                            variant="outline"
                            className="h-9 min-w-[140px] px-4 text-sm font-bold normal-case bg-white text-primary border-primary"
                            disabled={saving}
                            onClick={() => setPending(lockAllPending(entries, pending))}
                        >
                            {lockAllLabel(entries, pending)}
                        </Button>
                    ) : null}
                </Grid>
                {entries.length === 0 ? (
                    <Body2
                        css={css`
                            display: block;
                            color: ${GREY_TEXT};
                        `}
                    >
                        No resources scheduled on this day.
                    </Body2>
                ) : (
                    entries.map((entry) => (
                        <ResourceCard
                            key={resourceKey(entry)}
                            resource={entry.resource}
                            load={entry.load}
                            fill={entry.fill}
                            // The staged value, not the saved one, so the card mutes the moment it
                            // is locked rather than only after Save.
                            accepting={effectiveAccepting(entry, pending)}
                            lockable={!isPast}
                            locked={isLocked(entry, pending)}
                            lockDisabled={saving || toggleDisabled(entry, pending)}
                            onLockChange={(locked) =>
                                setPending((prev) => ({ ...prev, [resourceKey(entry)]: !locked }))
                            }
                        />
                    ))
                )}

                {isPast && (billing || aggregate.incomplete > 0) ? (
                    <BillingRow billing={billing} buckets={buckets} incomplete={aggregate.incomplete} />
                ) : null}
            </ModalContent>
            {isPast ? null : (
                <StickyModalActions>
                    <PrimaryButton
                        disabled={!dirty || saving}
                        onClick={save}
                        css={css`
                            max-width: 140px;
                        `}
                    >
                        Save
                    </PrimaryButton>
                </StickyModalActions>
            )}
        </ResponsiveSidebarDialog>
    );
}

export default DaySideDialog;
